import { createDomain } from 'effector'
import { useStoreMap } from 'effector-react'
import * as z from 'zod'
import { AxiosErrorType, Region } from '~/shared/api'
import { OptionEnum } from '~/shared/config/constants'
import { TimezoneCode } from '~/shared/config/enums'
import { createCache } from '~/shared/lib/mapCacheFactory'

export const formSchema = Region.schema.pick({
  name: true,
  timezone: true,
})

export type SchemaType = z.infer<typeof formSchema>
export type FormValues = Omit<SchemaType, 'timezone'> & {
  timezone: TimezoneCode | OptionEnum<TimezoneCode> | null
}

export const domain = createDomain('entities.taxipark.region')

export const requestFx = domain.createEffect<UniqueId, Region>({
  handler: fetchRegion,
})

export const saveFx = domain.createEffect<Region, Region, AxiosErrorType>({
  async handler(region) {
    await region.save()
    return fetchRegion(region.getApiId() as UniqueId)
  },
})

const {
  $cache: $regionsCache,
  useCache: useRegionCache,
  updateCache,
} = createCache<Region>({
  domain,
  getEntityId: (region) => region.getApiId() as UniqueId,
})
export { $regionsCache, useRegionCache }

$regionsCache
  .on(requestFx.doneData, (cache, region) => updateCache(cache, [region]))
  .on(saveFx.doneData, (cache, region) => updateCache(cache, [region], true))

export const $regionsError = domain
  .createStore<Record<UniqueId, Error>>({})
  .on([requestFx.fail], (store, { error, params: id }) => ({
    [id]: error,
    ...store,
  }))

export const useRegionError = (id: UniqueId) =>
  useStoreMap($regionsError, (errors) => errors[id])

async function fetchRegion(id: UniqueId) {
  const response = await Region.find(id)
  return response.getData() as Region
}
